import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from './store';
import { LearningPathProgressTypeState, LearningPathType, LearningPathTypeState } from '../common/learning-path';
import { deleteLearningPathService, getAllCardsPictureLearningPathService, getAllCardsTextLearningPathService, getMyStudentLearningPathService, getMyTeacherLearningPathService, guestCardPictureService, guestCardTextService, guestTypingCardPictureService, guestTypingCardTextService, incorrectGuestCardPictureService, incorrectGuestCardTextService, incorrectGuestTypingCardPictureService, incorrectGuestTypingCardTextService, learningPathDetailsService, learningPathProgressService, teacherCreateLearningPathService } from '../services/learningPathServices';
import { CardPicture, CardPictureVote, CardText } from '../common/card-model';


interface LearningPathsType {
	allTeacherLearningPaths: LearningPathType[];
	draftsLearningPaths: LearningPathTypeState[];
	studentIdDraftPath: string;
	draftsLearningPathDetails: Pick<LearningPathTypeState, "startAt" | "endAt" | "startTestAt" | "endTestAt" | "createBy" | "assignTo" | "category" | "categoryTags" | "dailyCardsFlip" | "flipCardVolume" | "dailyCardsLearn" | "learnedCardTypingAfterVolume" | "learnedCardVerbalAfterVolume" | "learningTypeTyping" | "learningTypeVerbal">
	allStudentLearningPaths: LearningPathType[];
	counts: number | null;
	pages: number;
	learningPathDetails: LearningPathType;
	loadingLearningPathDetails: boolean;
	errorLearningPathDetails: string | undefined;
	loadingGetLearningPaths: boolean;
	errorGetLearningPaths: string | undefined;
	errorCreateLearningPath: string | undefined;
	errorEditLearningPath: string | undefined;
	loadingCreateLearningPath: boolean
	loadingEditLearningPath: boolean;
	loadingDelete: boolean
	errorDelete: string | undefined;
	filteredCardsText: CardText[];
	loadingFetchAllCardsText: boolean;
	errorCardTextTags: string | undefined;
	randomId: number;
	usedIds: number[];
	guestOrNot: number;
	loadingUpdateCardText: boolean;
	errorUpdateCardText: string | undefined;
	updateCardPicturesError: string | undefined;
	updateCardPicturesLoading: boolean;
	filteredCardsPictures: CardPicture[];
	fetchAllCardsPicturesLoading: boolean;
	getCardPicturesError: string | undefined;
	loadingLearningPathProgressDetails: boolean;
	errorLearningPathProgressDetails: string | undefined;
	learningPathProgressDetails: {
		learningPath: LearningPathType;
		learningPathProgress: LearningPathProgressTypeState[];
	}
}

export const initialState: LearningPathsType = {
	allTeacherLearningPaths: [],
	draftsLearningPaths: [],
	studentIdDraftPath: "",
	draftsLearningPathDetails: {
		startAt: "",
		endAt: "",
		startTestAt: "",
		endTestAt: "",
		createBy: "",
		assignTo: "",
		category: "",
		categoryTags: [],
		dailyCardsFlip: 0,
		dailyCardsLearn: 0,
		flipCardVolume: 0,
		learnedCardVerbalAfterVolume: 0,
		learnedCardTypingAfterVolume: 0,
		learningTypeVerbal: false,
		learningTypeTyping: false,
	},
	allStudentLearningPaths: [],
	counts: null,
	pages: 0,
	learningPathDetails: {
		learningPathId: "",
		name: "",
		startAt: null,
		endAt: null,
		startTestAt: null,
		endTestAt: null,
		createBy: {
			id: "",
			firstName: "",
			lastName: "",
			avatarUrl: ""
		},
		assignTo: {
			id: "",
			firstName: "",
			lastName: "",
			avatarUrl: ""
		},
		category: {
			id: "",
			name: "",
			categoryType: ""
		},
		categoryTags: "",
		dailyCardsFlip: 0,
		dailyCardsLearn: 0,
		flipCardVolume: 0,
		learnedCardVerbalAfterVolume: 0,
		learnedCardTypingAfterVolume: 0,
		learningTypeVerbal: false,
		learningTypeTyping: false,
		completedVerbal: false,
		completedTyping: false
	},
	loadingLearningPathDetails: false,
	errorLearningPathDetails: undefined,
	loadingGetLearningPaths: false,
	errorGetLearningPaths: undefined,
	errorCreateLearningPath: undefined,
	errorEditLearningPath: undefined,
	loadingCreateLearningPath: false,
	loadingEditLearningPath: false,
	loadingDelete: false,
	errorDelete: undefined,
	filteredCardsText: [],
	loadingFetchAllCardsText: false,
	errorCardTextTags: undefined,
	randomId: 0,
	usedIds: [],
	guestOrNot: 0,
	loadingUpdateCardText: false,
	errorUpdateCardText: undefined,
	updateCardPicturesError: undefined,
	updateCardPicturesLoading: false,
	filteredCardsPictures: [],
	fetchAllCardsPicturesLoading: false,
	getCardPicturesError: undefined,
	loadingLearningPathProgressDetails: false,
	errorLearningPathProgressDetails: undefined,
	learningPathProgressDetails: {
		learningPath: {
			learningPathId: "",
			name: "",
			startAt: null,
			endAt: null,
			startTestAt: null,
			endTestAt: null,
			createBy: {
				id: "",
				firstName: "",
				lastName: "",
				avatarUrl: ""
			},
			assignTo: {
				id: "",
				firstName: "",
				lastName: "",
				avatarUrl: ""
			},
			category: {
				id: "",
				name: "",
				categoryType: ""
			},
			categoryTags: "",
			dailyCardsFlip: 0,
			dailyCardsLearn: 0,
			flipCardVolume: 0,
			learningTypeVerbal: false,
			learningTypeTyping: false,
			learnedCardVerbalAfterVolume: 0,
			learnedCardTypingAfterVolume: 0,
			completedVerbal: false,
			completedTyping: false
		},
		learningPathProgress: []
	}
}


interface GetLearningPathType {
	userId: string;
	page: number;
	sortBy?: 'alphabet' | 'date';
	sortOrder?: 'asc' | 'desc';
}

export const getTeacherLearningPaths = createAsyncThunk('learningPath/getTeacherLearningPaths', async ({ userId, page, sortBy, sortOrder }: GetLearningPathType) => {
	let response;
	try {
		response = await getMyTeacherLearningPathService(userId, page, sortBy, sortOrder)
	} catch (error) {
		console.error(error)
		throw error;
	}
	return response?.data
})

export const getStudentsLearningPaths = createAsyncThunk('learningPath/getStudentsLearningPaths', async ({ userId, page, sortBy, sortOrder }: GetLearningPathType) => {
	let response;
	try {
		response = await getMyStudentLearningPathService(userId, page, sortBy, sortOrder)
	} catch (error) {
		console.error(error)
		throw error;
	}
	return response?.data
})

export const teacherCreateLearningPath = createAsyncThunk(
	'learningPath/teacherCreateLearningPath',
	async (learningPaths: LearningPathTypeState[]) => {
		try {
			await teacherCreateLearningPathService(learningPaths); // Pass full array
		} catch (error) {
			console.error(error);
			throw error;
		}
	}
);

export const deleteLearningPath = createAsyncThunk('learningPath/deleteLearningPath', async (learningPathId: string) => {
	let response;
	try {
		response = await deleteLearningPathService(learningPathId)
	} catch (error) {
		console.error(error)
		throw error;
	}
	return response?.data
})

export const getLearningPathDetails = createAsyncThunk('learningPath/getLearningPathDetails', async (learningPathId: string) => {
	let response;
	try {
		response = await learningPathDetailsService(learningPathId)
	} catch (error) {
		console.error(error)
		throw error;
	}
	return response?.data
})

export const getCardsTextLearningPath = createAsyncThunk('learningPath/getCardsTextLearningPath', async (learningPathId: string) => {
	let response;
	try {
		response = await getAllCardsTextLearningPathService(learningPathId);
	} catch (error) {
		console.error(error);
		throw error;
	}
	return response?.data;
})

export const getCardsPictureLearningPath = createAsyncThunk('learningPath/getCardsPictureLearningPath', async (learningPathId: string) => {
	let response;
	try {
		response = await getAllCardsPictureLearningPathService(learningPathId);
	} catch (error) {
		console.error(error);
		throw error;
	}
	return response?.data;
})


export const guestCardText = createAsyncThunk('cardText/guestCardText', async ({
	id,
	categoryId,
	sideOne,
	sideTwo,
	guessesScore
}: Pick<CardText, "id" | "categoryId" | "sideOne" | "sideTwo" | "guessesScore">) => {
	let response;
	try {
		response = await guestCardTextService({
			id,
			categoryId,
			sideOne,
			sideTwo,
			guessesScore: guessesScore + 1
		})
	} catch (error) {
		console.error(error)
		throw error
	}
	return response?.data
})

export const notGuestCardText = createAsyncThunk('cardText/notGuestCardText', async ({
	id,
	categoryId,
	sideOne,
	sideTwo,
	incorrectGuessesScore
}: Pick<CardText, "id" | "categoryId" | "sideOne" | "sideTwo" | "incorrectGuessesScore">) => {
	let response;
	try {
		response = await incorrectGuestCardTextService({
			id,
			categoryId,
			sideOne,
			sideTwo,
			incorrectGuessesScore: incorrectGuessesScore + 1
		})
	} catch (error) {
		console.error(error)
		throw error
	}
	return response?.data
})

export const guestTypingCardText = createAsyncThunk('cardText/guestTypingCardText', async ({
	id,
	categoryId,
	sideOne,
	sideTwo,
	guessesScoreTyping
}: Pick<CardText, "id" | "categoryId" | "sideOne" | "sideTwo" | "guessesScoreTyping">) => {
	let response;
	try {
		response = await guestTypingCardTextService({
			id,
			categoryId,
			sideOne,
			sideTwo,
			guessesScoreTyping: guessesScoreTyping + 1
		})
	} catch (error) {
		console.error(error)
		throw error
	}
	return response?.data
})

export const notGuestTypingCardText = createAsyncThunk('cardText/notGuestTypingCardText', async ({
	id,
	categoryId,
	sideOne,
	sideTwo,
	incorrectGuessesScoreTyping
}: Pick<CardText, "id" | "categoryId" | "sideOne" | "sideTwo" | "incorrectGuessesScoreTyping">) => {
	let response;
	try {
		response = await incorrectGuestTypingCardTextService({
			id,
			categoryId,
			sideOne,
			sideTwo,
			incorrectGuessesScoreTyping: incorrectGuessesScoreTyping + 1
		})
	} catch (error) {
		console.error(error)
		throw error
	}
	return response?.data
})

export const guestCardPictures = createAsyncThunk('cardPicture/guestCardPictures', async ({
	id,
	categoryId,
	sideOne,
	sideTwo,
	guessesScore
}: Pick<CardPictureVote, "id" | "categoryId" | "sideOne" | "sideTwo" | "guessesScore">) => {
	let response;
	try {
		response = await guestCardPictureService({
			id,
			categoryId,
			sideOne,
			sideTwo,
			guessesScore: guessesScore + 1
		})
	} catch (error) {
		console.error(error)
		throw error
	}
	return response?.data
})

export const notGuestCardPictures = createAsyncThunk('cardPicture/notGuestCardPictures', async ({
	id,
	categoryId,
	sideOne,
	sideTwo,
	incorrectGuessesScore
}: Pick<CardPictureVote, "id" | "categoryId" | "sideOne" | "sideTwo" | "incorrectGuessesScore">) => {
	let response;
	try {
		response = await incorrectGuestCardPictureService({
			id,
			categoryId,
			sideOne,
			sideTwo,
			incorrectGuessesScore: incorrectGuessesScore + 1
		})
	} catch (error) {
		console.error(error)
		throw error
	}
	return response?.data
})

export const guestTypingCardPictures = createAsyncThunk('cardPicture/guestTypingCardPictures', async ({
	id,
	categoryId,
	sideOne,
	sideTwo,
	guessesScoreTyping
}: Pick<CardPictureVote, "id" | "categoryId" | "sideOne" | "sideTwo" | "guessesScoreTyping">) => {
	let response;
	try {
		response = await guestTypingCardPictureService({
			id,
			categoryId,
			sideOne,
			sideTwo,
			guessesScoreTyping: guessesScoreTyping + 1
		})
	} catch (error) {
		console.error(error)
		throw error
	}
	return response?.data
})

export const notGuestTypingCardPictures = createAsyncThunk('cardPicture/notGuestTypingCardPictures', async ({
	id,
	categoryId,
	sideOne,
	sideTwo,
	incorrectGuessesScoreTyping
}: Pick<CardPictureVote, "id" | "categoryId" | "sideOne" | "sideTwo" | "incorrectGuessesScoreTyping">) => {
	let response;
	try {
		response = await incorrectGuestTypingCardPictureService({
			id,
			categoryId,
			sideOne,
			sideTwo,
			incorrectGuessesScoreTyping: incorrectGuessesScoreTyping + 1
		})
	} catch (error) {
		console.error(error)
		throw error
	}
	return response?.data
})

export const learningPathProgress = createAsyncThunk('learningPath/learningPathProgress', async (learningPathId: string) => {
	let response;
	try {
		response = await learningPathProgressService(learningPathId)
	} catch (error) {
		console.error(error)
		throw error;
	}
	return response?.data
})

export const learningPathSlice = createSlice({
	name: 'learningPath',
	initialState,
	reducers: {
		cleanCreateError: (state: LearningPathsType) => {
			state.errorCreateLearningPath = undefined
		},
		cleanEditError: (state: LearningPathsType) => {
			state.errorEditLearningPath = undefined
		},
		cleanLearningPathDetails: (state: LearningPathsType) => {
			state.learningPathDetails.learningPathId = ""
			state.learningPathDetails.name = ""
			state.learningPathDetails.startAt = null
			state.learningPathDetails.endAt = null
			state.learningPathDetails.startTestAt = null
			state.learningPathDetails.endTestAt = null
			state.learningPathDetails.createBy = {
				id: "",
				firstName: "",
				lastName: "",
				avatarUrl: ""
			}
			state.learningPathDetails.assignTo = {
				id: "",
				firstName: "",
				lastName: "",
				avatarUrl: ""
			}
			state.learningPathDetails.category = {
				id: "",
				name: "",
				categoryType: ""
			}
			state.learningPathDetails.dailyCardsFlip = 0
			state.learningPathDetails.dailyCardsLearn = 0
			state.learningPathDetails.flipCardVolume = 0
			state.learningPathDetails.learnedCardVerbalAfterVolume = 0
			state.learningPathDetails.learnedCardTypingAfterVolume = 0
			state.learningPathDetails.learningTypeVerbal = false
			state.learningPathDetails.learningTypeTyping = false
			state.learningPathDetails.completedVerbal = false
			state.learningPathDetails.completedTyping = false

		},
		cleanDeleteCategoriesError: (state: LearningPathsType) => {
			state.errorDelete = undefined
		},
		cleanCategoryDetailsError: (state: LearningPathsType) => {
			state.errorGetLearningPaths = undefined
		},
		createDraftLearningPaths: (state: LearningPathsType, action) => {
			state.draftsLearningPaths = action.payload
		},
		cleanDraftLearningPaths: (state: LearningPathsType) => {
			state.draftsLearningPaths = []
		},
		editDraftLearningPath: (state: LearningPathsType, action) => {
			const index = state.draftsLearningPaths.findIndex(
				(draft) => draft.assignTo === action.payload.assignTo
			);

			if (index !== -1) {
				state.draftsLearningPaths[index] = action.payload;
			}
		},
		guessAnswerCorrect: (state: LearningPathsType) => {
			state.guestOrNot = 1
		},
		guessAnswerNotCorrect: (state: LearningPathsType) => {
			state.guestOrNot = -1
		},
		guessAnswerClearCorrect: (state: LearningPathsType) => {
			state.guestOrNot = 0
		},
		cleanFilteredCardsText: (state: LearningPathsType) => {
			state.filteredCardsText = []
		},
		cleanFilteredCardsPictures: (state: LearningPathsType) => {
			state.filteredCardsPictures = []
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(getTeacherLearningPaths.pending, (state: LearningPathsType) => {
				state.errorGetLearningPaths = undefined
				state.loadingGetLearningPaths = true
			})
			.addCase(getTeacherLearningPaths.fulfilled, (state: LearningPathsType, action) => {
				state.loadingGetLearningPaths = false
				state.counts = action.payload.count
				const pages = Math.ceil(action.payload.count / 20)
				state.pages = action.payload.count / 20 < 0 ? 2 : pages
				const allLearningPaths: LearningPathType[] = []
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				action.payload.results.forEach((path: any) => {
					const learningPathObject = {
						learningPathId: path.id,
						name: path.name,
						startAt: path.start_at,
						endAt: path.end_at,
						startTestAt: path.start_test_at,
						endTestAt: path.end_test_at,
						createBy: {
							id: path.created_by_user.id,
							firstName: path.created_by_user.first_name,
							lastName: path.created_by_user.last_name,
							avatarUrl: path.created_by_user.avatar_url
						},
						assignTo: {
							id: path.assigned_user.id,
							firstName: path.assigned_user.first_name,
							lastName: path.assigned_user.last_name,
							avatarUrl: path.assigned_user.avatar_url
						},
						category: {
							id: path.category.id,
							name: path.category.name,
							categoryType: path.category.category_type
						},
						categoryTags: path.categories_tags,
						dailyCardsFlip: path.daily_cards_flip_target,
						dailyCardsLearn: path.daily_cards_learn_target,
						flipCardVolume: path.flip_cards_volume,
						learningTypeVerbal: path.learning_type_verbal,
						learningTypeTyping: path.learning_type_typing,
						learnedCardVerbalAfterVolume: path.learned_card_volume_verbal,
						learnedCardTypingAfterVolume: path.learned_card_volume_typing,
						completedVerbal: path.completed_verbal,
						completedTyping: path.completed_typing
					}
					allLearningPaths.push(learningPathObject)
				})
				state.allTeacherLearningPaths = allLearningPaths
			})
			.addCase(getTeacherLearningPaths.rejected, (state: LearningPathsType, action) => {
				state.loadingGetLearningPaths = false
				state.errorGetLearningPaths = action.error.message
			})
		builder
			.addCase(getStudentsLearningPaths.pending, (state: LearningPathsType) => {
				state.errorGetLearningPaths = undefined
				state.loadingGetLearningPaths = true
			})
			.addCase(getStudentsLearningPaths.fulfilled, (state: LearningPathsType, action) => {
				state.loadingGetLearningPaths = false
				state.counts = action.payload.count
				const pages = Math.ceil(action.payload.count / 20)
				state.pages = action.payload.count / 20 < 0 ? 2 : pages
				const allLearningPaths: LearningPathType[] = []
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				action.payload.results.forEach((path: any) => {
					const learningPathObject = {
						learningPathId: path.id,
						name: path.name,
						startAt: path.start_at,
						endAt: path.end_at,
						startTestAt: path.start_test_at,
						endTestAt: path.end_test_at,
						createBy: {
							id: path.created_by_user.id,
							firstName: path.created_by_user.first_name,
							lastName: path.created_by_user.last_name,
							avatarUrl: path.created_by_user.avatar_url
						},
						assignTo: {
							id: path.assigned_user.id,
							firstName: path.assigned_user.first_name,
							lastName: path.assigned_user.last_name,
							avatarUrl: path.assigned_user.avatar_url
						},
						category: {
							id: path.category.id,
							name: path.category.name,
							categoryType: path.category.type_of_cards
						},
						categoryTags: path.categories_tags,
						dailyCardsFlip: path.daily_cards_flip_target,
						dailyCardsLearn: path.daily_cards_learn_target,
						flipCardVolume: path.flip_cards_volume,
						learningTypeVerbal: path.learning_type_verbal,
						learningTypeTyping: path.learning_type_typing,
						learnedCardVerbalAfterVolume: path.learned_card_volume_verbal,
						learnedCardTypingAfterVolume: path.learned_card_volume_typing,
						completedVerbal: path.completed_verbal,
						completedTyping: path.completed_typing
					}
					allLearningPaths.push(learningPathObject)
				})
				state.allStudentLearningPaths = allLearningPaths
			})
			.addCase(getStudentsLearningPaths.rejected, (state: LearningPathsType, action) => {
				state.loadingGetLearningPaths = false
				state.errorGetLearningPaths = action.error.message
			})
		builder
			.addCase(teacherCreateLearningPath.pending, (state: LearningPathsType) => {
				state.errorCreateLearningPath = undefined
				state.loadingCreateLearningPath = true
			})
			.addCase(teacherCreateLearningPath.fulfilled, (state: LearningPathsType) => {
				state.loadingCreateLearningPath = false
			})
			.addCase(teacherCreateLearningPath.rejected, (state: LearningPathsType, action) => {
				state.loadingCreateLearningPath = false
				state.errorCreateLearningPath = action.error.message
			})
		builder
			.addCase(deleteLearningPath.pending, (state: LearningPathsType) => {
				state.errorDelete = undefined
				state.loadingDelete = true
			})
			.addCase(deleteLearningPath.fulfilled, (state: LearningPathsType) => {
				state.loadingDelete = false
			})
			.addCase(deleteLearningPath.rejected, (state: LearningPathsType, action) => {
				state.loadingDelete = false
				state.errorDelete = action.error.message
			})
		builder
			.addCase(getLearningPathDetails.pending, (state: LearningPathsType) => {
				state.errorLearningPathDetails = undefined
				state.loadingLearningPathDetails = true
			})
			.addCase(getLearningPathDetails.fulfilled, (state: LearningPathsType, action) => {
				state.loadingLearningPathDetails = false
				state.learningPathDetails.learningPathId = action.payload.id;
				state.learningPathDetails.name = action.payload.name;
				state.learningPathDetails.startAt = action.payload.start_at;
				state.learningPathDetails.endAt = action.payload.end_at;
				state.learningPathDetails.startTestAt = action.payload.start_test_at;
				state.learningPathDetails.endTestAt = action.payload.end_test_at;

				state.learningPathDetails.createBy = {
					id: action.payload.created_by_user.id,
					firstName: action.payload.created_by_user.first_name,
					lastName: action.payload.created_by_user.last_name,
					avatarUrl: action.payload.created_by_user.avatar_url,
				};

				state.learningPathDetails.assignTo = {
					id: action.payload.assigned_user.id,
					firstName: action.payload.assigned_user.first_name,
					lastName: action.payload.assigned_user.last_name,
					avatarUrl: action.payload.assigned_user.avatar_url,
				};

				state.learningPathDetails.category = {
					id: action.payload.category.id,
					name: action.payload.category.name,
					categoryType: action.payload.category.type_of_cards,
				};
				state.learningPathDetails.categoryTags = action.payload.categories_tags;
				state.learningPathDetails.dailyCardsFlip = action.payload.daily_cards_flip_target;
				state.learningPathDetails.dailyCardsLearn = action.payload.daily_cards_learn_target;
				state.learningPathDetails.flipCardVolume = action.payload.flip_cards_volume;
				state.learningPathDetails.learningTypeVerbal = action.payload.learning_type_verbal;
				state.learningPathDetails.learningTypeTyping = action.payload.learning_type_typing;
				state.learningPathDetails.learnedCardVerbalAfterVolume = action.payload.learned_card_volume_verbal;
				state.learningPathDetails.learnedCardTypingAfterVolume = action.payload.learned_card_volume_typing;
				state.learningPathDetails.completedVerbal = action.payload.completed_verbal;
				state.learningPathDetails.completedTyping = action.payload.completed_typing;


			})
			.addCase(getLearningPathDetails.rejected, (state: LearningPathsType, action) => {
				state.loadingLearningPathDetails = false
				state.errorLearningPathDetails = action.error.message
			})
		builder
			.addCase(getCardsTextLearningPath.pending, (state: LearningPathsType) => {
				state.errorCardTextTags = undefined
				state.loadingFetchAllCardsText = true
			})
			.addCase(getCardsTextLearningPath.fulfilled, (state: LearningPathsType, action) => {
				state.loadingFetchAllCardsText = false
				const allCardsText: CardText[] = []
				action.payload.forEach((card: any) => {
					const cartTextObject = {
						id: card.id,
						sideOne: card.first_side,
						sideTwo: card.second_side,
						categoryId: card.category,
						createdAt: card.created_at,
						updateAt: card.updated_at,
						guessesScore: card.guesses_score,
						incorrectGuessesScore: card.incorrect_guesses_score,
						guessesScoreTyping: card.guesses_score_typing,
						incorrectGuessesScoreTyping: card.incorrect_guesses_score_typing,
						tag: card.tag
					}
					allCardsText.push(cartTextObject)
				})
				state.filteredCardsText = allCardsText;
				if (state.usedIds.length >= state.filteredCardsText.length) {
					state.usedIds = []
				}
				let newId;
				do {
					newId = Math.floor(Math.random() * state.filteredCardsText.length);
				} while (state.usedIds.includes(newId));
				state.usedIds.push(newId);
				state.randomId = newId;
			})
			.addCase(getCardsTextLearningPath.rejected, (state: LearningPathsType, action) => {
				state.loadingFetchAllCardsText = false
				state.errorCardTextTags = action.error.message
			})
		builder
			.addCase(guestCardText.pending, (state: LearningPathsType) => {
				state.errorUpdateCardText = undefined
				state.loadingUpdateCardText = true
			})
			.addCase(guestCardText.fulfilled, (state: LearningPathsType) => {
				state.loadingUpdateCardText = false
			})
			.addCase(guestCardText.rejected, (state: LearningPathsType, action) => {
				state.loadingUpdateCardText = false
				state.errorUpdateCardText = action.error.message
			})
		builder
			.addCase(notGuestCardText.pending, (state: LearningPathsType) => {
				state.errorUpdateCardText = undefined
				state.loadingUpdateCardText = true
			})
			.addCase(notGuestCardText.fulfilled, (state: LearningPathsType) => {
				state.loadingUpdateCardText = false
			})
			.addCase(notGuestCardText.rejected, (state: LearningPathsType, action) => {
				state.loadingUpdateCardText = false
				state.errorUpdateCardText = action.error.message
			})
		builder
			.addCase(guestTypingCardText.pending, (state: LearningPathsType) => {
				state.errorUpdateCardText = undefined
				state.loadingUpdateCardText = true
			})
			.addCase(guestTypingCardText.fulfilled, (state: LearningPathsType) => {
				state.loadingUpdateCardText = false
			})
			.addCase(guestTypingCardText.rejected, (state: LearningPathsType, action) => {
				state.loadingUpdateCardText = false
				state.errorUpdateCardText = action.error.message
			})
		builder
			.addCase(notGuestTypingCardText.pending, (state: LearningPathsType) => {
				state.errorUpdateCardText = undefined
				state.loadingUpdateCardText = true
			})
			.addCase(notGuestTypingCardText.fulfilled, (state: LearningPathsType) => {
				state.loadingUpdateCardText = false
			})
			.addCase(notGuestTypingCardText.rejected, (state: LearningPathsType, action) => {
				state.loadingUpdateCardText = false
				state.errorUpdateCardText = action.error.message
			})
		builder
			.addCase(guestCardPictures.pending, (state: LearningPathsType) => {
				state.updateCardPicturesError = undefined
				state.updateCardPicturesLoading = true
			})
			.addCase(guestCardPictures.fulfilled, (state: LearningPathsType) => {
				state.updateCardPicturesLoading = false
			})
			.addCase(guestCardPictures.rejected, (state: LearningPathsType, action) => {
				state.updateCardPicturesLoading = false
				state.updateCardPicturesError = action.error.message
			})
		builder
			.addCase(notGuestCardPictures.pending, (state: LearningPathsType) => {
				state.updateCardPicturesError = undefined
				state.updateCardPicturesLoading = true
			})
			.addCase(notGuestCardPictures.fulfilled, (state: LearningPathsType) => {
				state.updateCardPicturesLoading = false
			})
			.addCase(notGuestCardPictures.rejected, (state: LearningPathsType, action) => {
				state.updateCardPicturesLoading = false
				state.updateCardPicturesError = action.error.message
			})
		builder
			.addCase(guestTypingCardPictures.pending, (state: LearningPathsType) => {
				state.updateCardPicturesError = undefined
				state.updateCardPicturesLoading = true
			})
			.addCase(guestTypingCardPictures.fulfilled, (state: LearningPathsType) => {
				state.updateCardPicturesLoading = false
			})
			.addCase(guestTypingCardPictures.rejected, (state: LearningPathsType, action) => {
				state.updateCardPicturesLoading = false
				state.updateCardPicturesError = action.error.message
			})
		builder
			.addCase(notGuestTypingCardPictures.pending, (state: LearningPathsType) => {
				state.updateCardPicturesError = undefined
				state.updateCardPicturesLoading = true
			})
			.addCase(notGuestTypingCardPictures.fulfilled, (state: LearningPathsType) => {
				state.updateCardPicturesLoading = false
			})
			.addCase(notGuestTypingCardPictures.rejected, (state: LearningPathsType, action) => {
				state.updateCardPicturesLoading = false
				state.updateCardPicturesError = action.error.message
			}),
			builder
				.addCase(getCardsPictureLearningPath.pending, (state: LearningPathsType) => {
					state.getCardPicturesError = undefined
					state.fetchAllCardsPicturesLoading = true
				})
				.addCase(getCardsPictureLearningPath.fulfilled, (state: LearningPathsType, action) => {
					state.fetchAllCardsPicturesLoading = false
					const allCardsPictures: CardPicture[] = []
					action.payload.forEach((card: any) => {
						const cartTextObject = {
							id: card.id,
							sideOne: card.first_side_url,
							sideTwo: card.second_side,
							categoryId: card.category,
							createdAt: card.created_at,
							updateAt: card.updated_at,
							guessesScore: card.guesses_score,
							incorrectGuessesScore: card.incorrect_guesses_score,
							guessesScoreTyping: card.guesses_score_typing,
							incorrectGuessesScoreTyping: card.incorrect_guesses_score_typing,
							tag: card.tag
						}
						allCardsPictures.push(cartTextObject)
					})
					state.filteredCardsPictures = allCardsPictures
					if (state.usedIds.length >= state.filteredCardsPictures.length) {
						state.usedIds = []
					}
					let newId;
					do {
						newId = Math.floor(Math.random() * state.filteredCardsPictures.length);
					} while (state.usedIds.includes(newId));
					state.usedIds.push(newId);
					state.randomId = newId;
				}),
			builder
				.addCase(learningPathProgress.pending, (state: LearningPathsType) => {
					state.errorLearningPathProgressDetails = undefined
					state.loadingLearningPathProgressDetails = true
				})
				.addCase(learningPathProgress.fulfilled, (state: LearningPathsType, action) => {
					state.loadingLearningPathProgressDetails = false
					state.learningPathProgressDetails.learningPath = {
						learningPathId: action.payload.id,
						name: action.payload.name,
						startAt: action.payload.start_at,
						endAt: action.payload.end_at,
						startTestAt: action.payload.start_test_at,
						endTestAt: action.payload.end_test_at,
						createBy: {
							id: action.payload.created_by_user.id,
							firstName: action.payload.created_by_user.first_name,
							lastName: action.payload.created_by_user.last_name,
							avatarUrl: action.payload.created_by_user.avatar_url
						},
						assignTo: {
							id: action.payload.assigned_user.id,
							firstName: action.payload.assigned_user.first_name,
							lastName: action.payload.assigned_user.last_name,
							avatarUrl: action.payload.assigned_user.avatar_url
						},
						category: {
							id: action.payload.category.id,
							name: action.payload.category.name,
							categoryType: action.payload.category.type_of_cards
						},
						categoryTags: action.payload.categories_tags,
						dailyCardsFlip: action.payload.daily_cards_flip_target,
						dailyCardsLearn: action.payload.daily_cards_learn_target,
						flipCardVolume: action.payload.flip_cards_volume,
						learningTypeVerbal: action.payload.learning_type_verbal,
						learningTypeTyping: action.payload.learning_type_typing,
						learnedCardVerbalAfterVolume: action.payload.learned_card_volume_verbal,
						learnedCardTypingAfterVolume: action.payload.learned_card_volume_typing,
						completedVerbal: action.payload.completed_verbal,
						completedTyping: action.payload.completed_typing
					}
					const allCardsProgress: LearningPathProgressTypeState[] = []
					action.payload.progress.forEach((progress: any) => {
						const progressObject = {
							learningPathId: progress.id,
							userId: progress.id,
							createAt: progress.create_at,
							learnedCardsTyping: progress.learned_cards_typing,
							learnedCardsVerbal: progress.learned_cards_verbal,
							flipCardsVerbal: progress.flip_cards_verbal,
							flipCardsTyping: progress.flip_cards_typing,
						}
						allCardsProgress.push(progressObject)
					})
					state.learningPathProgressDetails.learningPathProgress = allCardsProgress
					if (state.usedIds.length >= state.filteredCardsPictures.length) {
						state.usedIds = []
					}
					let newId;
					do {
						newId = Math.floor(Math.random() * state.filteredCardsPictures.length);
					} while (state.usedIds.includes(newId));
					state.usedIds.push(newId);
					state.randomId = newId;
				})
				.addCase(learningPathProgress.rejected, (state: LearningPathsType, action) => {
					state.loadingLearningPathProgressDetails = false
					state.errorLearningPathProgressDetails = action.error.message
				})
	}

})

export const actions = learningPathSlice.actions;

export const learningPathSelector = (state: RootState) => state[learningPathSlice.name];